/** Digits only; default India 91 for 10-digit local numbers (WhatsApp wa.me format, no +). */
export function sanitizePhoneForWhatsApp(raw) {
  let d = String(raw || "").replace(/\D/g, "");
  if (!d) return "";
  if (d.startsWith("0")) d = d.replace(/^0+/, "") || "";
  if (d.length === 10) return `91${d}`;
  return d;
}

export function whatsAppSendUrl(phoneDigits, message) {
  const text = encodeURIComponent(message);
  if (phoneDigits && phoneDigits.length >= 10) {
    return `https://wa.me/${phoneDigits}?text=${text}`;
  }
  return `https://wa.me/?text=${text}`;
}

export function buildNetPayWhatsAppText({
  personName,
  autoCut,
  manualCut,
  net,
  periodLabel,
  branchLabel,
  fyLabel,
  fmt,
}) {
  return [
    "Dear " + personName + ",",
    "",
    "Please find your Monthly Sales Incentive details below:",
    `Period: ${periodLabel}`,
    `Branch: ${branchLabel}`,
    `Financial Year: ${fyLabel}`,
    "",
    `Leave cut: ${fmt(autoCut)}`,
    `Late coming cut amount: ${fmt(manualCut)}`,
    `Net Payable Incentive: ${fmt(net)}`,
    "",
    "Kindly review and contact Accounts for any clarification.",
    "",
    "Regards,",
    "Accounts Department",
  ].join("\n");
}

export function parseStaffLineCuts(line) {
  const hasSplit = line?.autoCut != null || line?.manualCut != null;
  return {
    leaveCut: hasSplit ? Number(line.autoCut) || 0 : Number(line.cut) || 0,
    cutAmount: hasSplit ? Number(line.manualCut) || 0 : 0,
  };
}

export function buildStaffAllocationSummaryText({
  staffLines,
  periodLabel,
  branchLabel,
  fyLabel,
  totalFinal,
  fmt,
}) {
  const lines = [
    "Monthly Sales Incentive — Staff Allocation",
    `Period: ${periodLabel}`,
    `Branch: ${branchLabel}`,
    `Financial Year: ${fyLabel}`,
    "",
  ];
  staffLines.forEach((L, i) => {
    const { leaveCut, cutAmount } = parseStaffLineCuts(L);
    const brand = L.brandId || "—";
    const pct = L.pct != null && L.pct !== "" ? `${L.pct}%` : "—";
    lines.push(
      `${i + 1}. ${L.name} — ${brand} (${pct})`,
      `   Leave: ${L.leave ?? "—"} | Gross: ${fmt(L.base)} | Leave cut: ${fmt(leaveCut)} | Late cut: ${fmt(cutAmount)} | Branding share: ${fmt(L.poolShare)} | Final: ${fmt(L.finalAmt)}`
    );
  });
  lines.push("");
  lines.push(`Total net pay (all staff): ${fmt(totalFinal)}`);
  return lines.join("\n");
}

export function buildStaffLineWhatsAppText({
  personName,
  brandId,
  autoCut,
  manualCut,
  finalAmt,
  periodLabel,
  branchLabel,
  fyLabel,
  fmt,
}) {
  const brandLine = brandId ? `Brand allocation: ${brandId}` : "";
  return [
    "Dear " + personName + ",",
    "",
    "Please find your Monthly Sales Incentive details below:",
    brandLine,
    `Period: ${periodLabel}`,
    `Branch: ${branchLabel}`,
    `Financial Year: ${fyLabel}`,
    "",
    `Leave cut: ${fmt(autoCut)}`,
    `Late coming cut amount: ${fmt(manualCut)}`,
    `Net Payable Incentive: ${fmt(finalAmt)}`,
    "",
    "Kindly review and contact Accounts for any clarification.",
    "",
    "Regards,",
    "Accounts Department",
  ]
    .filter(Boolean)
    .join("\n");
}

export function buildStaffPoolPctWhatsAppText({
  personName,
  ratePct,
  othersTotal,
  amount,
  periodLabel,
  branchLabel,
  fyLabel,
  fmt,
  fmtPct,
}) {
  return [
    "Dear " + personName + ",",
    "",
    "Please find your Monthly Sales Incentive details below:",
    `Period: ${periodLabel}`,
    `Branch: ${branchLabel}`,
    `Financial Year: ${fyLabel}`,
    "",
    `Incentive: ${fmtPct(ratePct)} of all other staff (base ${fmt(othersTotal)})`,
    `Net Payable Incentive: ${fmt(amount)}`,
    "",
    "Kindly review and contact Accounts for any clarification.",
    "",
    "Regards,",
    "Accounts Department",
  ].join("\n");
}

export const WHATSAPP_ICON_PATH =
  "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z";

/** Stable key for one staff allocation row (WhatsApp sent tracking). */
export function staffLineWhatsAppKey(line, index = 0) {
  const eid = String(line?.employeeId || "").trim().toLowerCase();
  const name = String(line?.name || "").trim().toLowerCase();
  const brand = String(line?.brandId || "").trim().toLowerCase();
  const idx = Number.isFinite(Number(line?.index)) ? Number(line.index) : Number(index) || 0;
  return `${eid || name || "row"}|${brand}|${idx}`;
}

/** Blue double-tick paths (WhatsApp-style sent marker). */
export const WHATSAPP_BLUE_TICK_PATHS = [
  "M11.07 1.14 5.4 7.3 2.93 4.7 1.8 5.9l3.6 3.9 6.8-7.4-1.13-1.26Z",
  "M14.2 1.14 8.53 7.3 7.6 6.3l-1.13 1.2 2.06 2.3 6.8-7.4-1.13-1.26Z",
];
